import { includes } from 'lodash/fp';

import config from '../config';
import constants from '../ahm-constants';
import encryptString from '../pages/buy/components/buy-form/utils/encrypt-string';
import getWebCryptoSubtle from '../utils/web-crypto';
import logging from '../logging';

const { PRODUCT_TYPE, SCALE } = constants;

//            single  s.parent  couple  family
// combined   2       4         4       4
// package    2       4         4       4
// hospital   1       2         2       2
// extras     1       2         2       2
// ambulance  1       2         2       2
const getPseu = (type: string, scale: string): number => {
  const typeMultiplier = includes(type, [PRODUCT_TYPE.COMBINED, PRODUCT_TYPE.PACKAGE]) ? 2 : 1;
  const scaleMultiplier = scale === SCALE.SINGLE ? 1 : 2;
  return typeMultiplier * scaleMultiplier;
};

// The following functions are used to format and normalise email addresses and phone numbers according to Google and other non Google specifications.
// To learn more about the formatting requirements, see: https://medibank.atlassian.net/wiki/spaces/MD/pages/620332291/Hash+normalisation+investigation+SHA256+Algorithm

/**
 * Normalises an email address by trimming and converting to lowercase.
 * Returns empty string if no data is provided.
 *
 * @param {string} email - The email address to normalise.
 * @returns {string} The normalised email address or empty string.
 */
const normaliseEmail = (email: string): string => {
  if (!email) {
    return '';
  }
  return email.trim().toLowerCase();
};

/**
 * As per https://developers.google.com/google-ads/api/docs/conversions/upload-online#prepare-data
 * Formats a Google email address by:
 * - Removing leading and trailing whitespaces
 * - Converting to lowercase
 * - Removing all periods before the @ sign
 * - Returns empty string if no data provided.
 *
 * @param {string} email - The email address to format.
 * @returns {string} The formatted email address or empty string.
 *
 * @example
 * formatGoogleEmailAddress('  John.Smith@gmail.com  ') // returns 'johnsmith@gmail.com'
 */
export const formatGoogleEmailAddress = (email: string): string => {
  const normalised = normaliseEmail(email);

  if (!normalised) {
    return '';
  }

  // Split by @ to separate local part from domain
  const [localPart, domain] = normalised.split('@');

  if (!localPart || !domain) {
    return normalised;
  }

  // Remove all periods from the local part (before @)
  const localPartWithoutDots = localPart.replace(/\./g, '');

  // Reconstruct the email
  return `${localPartWithoutDots}@${domain}`;
};

/**
 * As per https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters#client-ip-address
 * Formats a non-Google email address by:
 * - Removing leading and trailing whitespaces
 * - Converting to lowercase
 * - Returns empty string if no data provided.
 *
 * @param {string} email - The email address to format.
 * @returns {string} The formatted email address or empty string.
 *
 * @example
 * formatNonGoogleEmailAddress('  John_Smith@gmail.com  ') // returns 'john_smith@gmail.com'
 */
export const formatNonGoogleEmailAddress = (email: string): string => normaliseEmail(email);

/**
 * Normalises a phone number by removing all non-digit characters and leading zeros.
 * Returns empty string if no data is provided.
 *
 * @param {string} phone - The phone number to normalise.
 * @returns {string} The normalised phone number or empty string.
 */
const normalisePhoneNumber = (phone: string): string => {
  if (!phone) {
    return '';
  }
  // Remove all non-digit characters
  let digits = phone.replace(/\D/g, '');
  // Remove leading zeros
  digits = digits.replace(/^0+/, '');
  return digits;
};

/**
 * As per https://developers.google.com/google-ads/api/docs/conversions/upload-online#prepare-data
 * Formats a Google phone number according to E164 standard.
 * - Removes symbols, letters, and leading zeros.
 * - Adds + and country code (assume AU: 61 if not present).
 * - Returns empty string if no data provided.
 *
 * @param {string} phone - The phone number to format.
 * @returns {string} The formatted phone number or empty string.
 *
 * @example
 * formatGooglePhoneNumber('0432472817') // returns '+61432472817'
 */
export const formatGooglePhoneNumber = (phone: string): string => {
  const normalised = normalisePhoneNumber(phone);
  if (!normalised) {
    return '';
  }
  // If already starts with country code (e.g. 61...), just add +
  if (normalised.startsWith('61')) {
    return `+${normalised}`;
  }
  // Otherwise, prepend country code 61 (AU)
  return `+61${normalised}`;
};

/**
 * As per https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters#client-ip-address
 * Formats a non-Google phone number according to E164 standard.
 * - Removes symbols, letters, and leading zeros.
 * - Does NOT add + or country code.
 * - Returns empty string if no data provided.
 *
 * @param {string} phone - The phone number to format.
 * @returns {string} The formatted phone number or empty string.
 *
 * @example
 * formatNonGooglePhoneNumber('0432472817') // returns '61432472817'
 */
export const formatNonGooglePhoneNumber = (phone: string): string => {
  const normalised = normalisePhoneNumber(phone);
  if (!normalised) {
    return '';
  }
  // If already starts with country code (e.g. 61...), return as is
  if (normalised.startsWith('61')) {
    return normalised;
  }
  // Otherwise, prepend country code 61 (AU)
  return `61${normalised}`;
};

const arrayBufferToHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

/**
 * Hashes a string using SHA-256 and returns the hex digest.
 * Returns the hash of the input string, or the hash of an empty string if input is empty.
 *
 * @param {string} value - The string to hash.
 * @returns {Promise<string>} The SHA-256 hash as a hex string.
 */
export const sha256Hash = async (value: string): Promise<string> => {
  const subtleCrypto = getWebCryptoSubtle();
  const encodedValue = new TextEncoder().encode(value);
  const digest = await subtleCrypto.digest('SHA-256', encodedValue);

  return arrayBufferToHex(digest);
};

/**
 * Hashes the input string using SHA-256, then encrypts the hash with the Google Tag Manager public key from config.
 * Returns empty string and logs when hashing/encryption cannot be completed.
 *
 * @param {string} value - The string to hash and encrypt.
 * @returns {Promise<string>} The encrypted SHA-256 hash of the input string.
 */
export const encryptAndHashGTMPayload = async (value: string): Promise<string> => {
  try {
    const publicKeyPem: string = config.get('google:tagManager:encryptionPublicKey');

    if (!publicKeyPem) {
      throw new Error('Encryption public key is missing from config.');
    }

    const hashed = await sha256Hash(value);

    return await encryptString(publicKeyPem, hashed);
  } catch (error) {
    logging.getLogger().error('Failed to hash and encrypt GTM payload', {
      error,
    });
    return '';
  }
};

export const encryptUserData = async (email: string | undefined, phoneNumber: string | undefined) => {
  const [googleFormatEmail, nonGoogleFormatEmail, googleFormatPhoneNumber, nonGoogleFormatPhoneNumber] =
    await Promise.all([
      email ? encryptAndHashGTMPayload(formatGoogleEmailAddress(email)) : '',
      email ? encryptAndHashGTMPayload(formatNonGoogleEmailAddress(email)) : '',
      phoneNumber ? encryptAndHashGTMPayload(formatGooglePhoneNumber(phoneNumber)) : '',
      phoneNumber ? encryptAndHashGTMPayload(formatNonGooglePhoneNumber(phoneNumber)) : '',
    ]);
  /* eslint-disable camelcase */
  // snake_case keys are used here as per GA spec
  return {
    sha256_email_address_google: googleFormatEmail,
    sha256_email_address_non_google: nonGoogleFormatEmail,
    sha256_phone_number_google: googleFormatPhoneNumber,
    sha256_phone_number_non_google: nonGoogleFormatPhoneNumber,
  };
  /* eslint-enable camelcase */
};

export default {
  encryptAndHashGTMPayload,
  encryptUserData,
  formatGoogleEmailAddress,
  formatGooglePhoneNumber,
  formatNonGoogleEmailAddress,
  formatNonGooglePhoneNumber,
  getPseu,
  sha256Hash,
};
